import { UserRole } from 'app-types';
import { useGetDeskInfo, useGetSubscriptionInfo, useGetUser } from 'data-layer';
import {
  ChatSidebarIcon,
  DeskSidebarIcon,
  HomeIcon,
  InvoiceChecking,
  MultipleSettings,
} from 'icons';
import { FormattedMessage } from 'localization';

import {
  EnumAdvancedToolsRoutes,
  EnumDeskRoutes,
  EnumGeneralSettingRoutes,
  EnumSettingRoutes,
  InvoiceCheckingTabRoutes,
} from '../../constants';
import { SettingsSidebarMenuItem } from './AdminSettingsSidebar';
import { useBrandingSection } from './sidebar-options/brandingSection';
import { chatSection } from './sidebar-options/chatSection';
import { adminRoles, deskSection } from './sidebar-options/deskSection';
import { fulfillmentSection } from './sidebar-options/fulfillmentSection';
import { generalSettingsSection } from './sidebar-options/generalSettingsSection';
import { notificationsSection } from './sidebar-options/notificationsSection';
import { returnsAndExchangesSection } from './sidebar-options/returnsAndExchangesSection';
import { shippingSection } from './sidebar-options/shippingSection';
import { threePLProviderSection, threePLSection } from './sidebar-options/threePLSection';
import { trackingSection } from './sidebar-options/trackingSection';

const useGetAdminMenuItems = () => {
  const { brandingSection } = useBrandingSection();
  const { data: userData, isLoading: isLoadingUser } = useGetUser();
  const { data: subscriptionInfo, isLoading: isLoadingSubscription } = useGetSubscriptionInfo();
  const { hasActivatedDesk } = useGetDeskInfo(subscriptionInfo?.subscription);

  const menuItems: SettingsSidebarMenuItem[] = [];

  const isAdmin = adminRoles.some((role) => userData?.roles.includes(role));

  if (subscriptionInfo?.subscription.isActive) {
    if (isAdmin) {
      // Full access for active subscriptions
      menuItems.push(fulfillmentSection);
      menuItems.push(shippingSection);
      menuItems.push(trackingSection);
      menuItems.push(notificationsSection);
      menuItems.push(returnsAndExchangesSection);
      menuItems.push({
        translationKey: 'dashboard.invoiceChecking',
        data: [
          {
            value: userData?.storeSettings.featureFlags?.enableNewUI
              ? `/${InvoiceCheckingTabRoutes.invoice}/${InvoiceCheckingTabRoutes.checking}`
              : '/cms/invoice/settings',
            label: <FormattedMessage id="settings.invoice-settings" />,
          },
        ],
        hasDivider: true,
        oldApp: !userData?.storeSettings.featureFlags?.enableNewUI,
        icon: <InvoiceChecking width={24} height={24} />,
      });
    }
  }

  if (hasActivatedDesk) {
    menuItems.push({
      icon: <DeskSidebarIcon width={24} height={24} />,
      data: [...deskSection].filter(
        (item) =>
          !item.roles || (item.roles && item.roles.some((role) => userData?.roles.includes(role))),
      ),
      title: 'Outvio Desk',
    });

    if (adminRoles.some((role) => userData?.roles.includes(role))) {
      menuItems.push({
        hasDivider: !!userData?.storeSettings.is3pl,
        icon: <ChatSidebarIcon width={24} height={24} />,
        data: chatSection,
        translationKey: 'general.outvio-chat',
      });
    }
  }

  if (subscriptionInfo?.subscription.isActive) {
    const isCustomerSupport = userData?.roles.includes(UserRole.customerSupport);
    if (isAdmin || isCustomerSupport) {
      menuItems.push({
        translationKey: 'general.advanced-tools',
        icon: <MultipleSettings width={24} height={24} />,
        data: [
          {
            value: `/${EnumDeskRoutes.settings}/${EnumSettingRoutes.advancedTools}/${EnumAdvancedToolsRoutes.cashOnDelivery}`,
            label: <FormattedMessage id="general.cash-on-delivery" />,
          },
          {
            value: `/${EnumDeskRoutes.settings}/${EnumSettingRoutes.advancedTools}/${EnumAdvancedToolsRoutes.ordersExport}`,
            label: <FormattedMessage id="settings.adv-tools.orders-export.title" />,
          },
        ],
      });
    }

    if (isAdmin) {
      menuItems.push({
        data: userData?.storeSettings.is3pl ? threePLProviderSection : threePLSection,
        title: '3PL',
        icon: <HomeIcon width={24} height={24} />,
        hasDivider: true,
      });
    }
  }

  if (!subscriptionInfo?.subscription.isActive) {
    if (isAdmin) {
      // Remove user management and integrations for inactive subscriptions
      menuItems.push({
        ...generalSettingsSection,
        data: generalSettingsSection.data?.filter(
          (item) =>
            !item.value.includes(EnumGeneralSettingRoutes.userManagement) &&
            !item.value.includes(EnumGeneralSettingRoutes.integrations),
        ),
      });
    } else {
      menuItems.push({
        ...generalSettingsSection,
        data: generalSettingsSection.data?.filter(
          (item) => !item.roles || item.roles.some((role) => userData?.roles.includes(role)),
        ),
      });
    }
  } else {
    if (isAdmin) {
      menuItems.push(brandingSection);
      menuItems.push(generalSettingsSection);
    } else {
      // Only show items matching user roles
      menuItems.push({
        ...generalSettingsSection,
        data: generalSettingsSection.data?.filter(
          (item) => !item.roles || item.roles.some((role) => userData?.roles.includes(role)),
        ),
      });
    }
  }

  return { menuItems, isLoading: isLoadingSubscription || isLoadingUser };
};

export { useGetAdminMenuItems };
